<script setup lang="ts">
import BaseButton from '@/components/base/BaseButton.vue'
import { T } from '@tolgee/vue'
import ThePremium from '@/components/ThePremium.vue'
import TonDiscountBadge from '@/components/TonDiscountBadge.vue'
import { formatUnshortenedNumber } from '@/helpers/utils'
import { ECOMMERCE_ITEM_PRICE_TYPE, type EcommerceItem } from '@/types/shared/player-config'
import type { EcommercePrizeCardItem } from '@/types/shop'
import { type PurchaseMetadata, useEcommerceItemDetailsPurchase } from '@/composables/game/ecommerce'
import { computed, ref } from 'vue'
import { usePlayer } from '@/composables/game/player'

const props = defineProps<{
	item: EcommerceItem | EcommercePrizeCardItem
	purchaseMetadata?: PurchaseMetadata
	tonButtonDarkOutlineStyle?: boolean
}>()

const emit = defineEmits<{
	(e: 'purchase', result: { paid: boolean, nonce?: string }): void
}>()

const {
  starPrice,
  tonPrice,
  dropeePrice,
  usdtPrice,
  isBuyingWithStars,
  isBuyingWithTon,
  isBuyingWithDropee,
  isBuyingWithUsdt,
  showTonPurchase,
  onPurchase,
  selectedPriceType,
} = useEcommerceItemDetailsPurchase(props.item)

const isLoading = ref(false)

const { player, isBasePlatform } = usePlayer()
const starsBalance = computed(() => player.value?.ecommerce?.starsBalance ?? 0)

async function handlePurchase(priceType: ECOMMERCE_ITEM_PRICE_TYPE) {
  isLoading.value = true
  try {
    const result = await onPurchase(priceType, props.purchaseMetadata)
    if (result.paid) {
      setTimeout(() => {
        emit('purchase', result)
        isLoading.value = false
      }, priceType === ECOMMERCE_ITEM_PRICE_TYPE.STAR ? 350 : 1)
    } else {
      isLoading.value = false
      selectedPriceType.value = null
    }
  } catch (e) {
    isLoading.value = false
    selectedPriceType.value = null
  }
}
</script>

<template>
  <div class="w-full flex flex-col gap-y-3">
    <div
      v-if="dropeePrice && !isBasePlatform"
      class="relative w-full"
    >
      <div class="z-[1] absolute right-3 -top-1 rotate-[20deg]">
        <TonDiscountBadge label="-20%" />
      </div>
      <BaseButton
        style-type="primary-pink"
        :loading="isBuyingWithDropee"
        class="relative w-full !text-black"
        @click="handlePurchase(ECOMMERCE_ITEM_PRICE_TYPE.DROPEE)"
      >
        <span
          v-if="!isBuyingWithDropee"
          class="flex items-center gap-x-2"
        >
          <T
            key-name="starter_pack.buy_with"
            default-value="Buy with"
          />
          <img
            src="@/assets/images/dropee-token-new.webp"
            class="size-5 min-w-[20px] object-cover"
            alt="Dropee token"
          >
          <span>{{ dropeePrice?.amount }}</span>
        </span>
        <T
          v-else
          key-name="lingo.giveaway.transaction.loading_cta"
          default-value="Processing..."
        />
      </BaseButton>
    </div>

    <BaseButton
      v-if="starPrice.amount === 0"
      class="w-full"
      :loading="isBuyingWithStars || (isLoading && selectedPriceType === ECOMMERCE_ITEM_PRICE_TYPE.STAR)"
      :class="(isBuyingWithStars || isLoading) && 'pointer-events-none'"
      @click="handlePurchase(ECOMMERCE_ITEM_PRICE_TYPE.STAR)"
    >
      <T
        v-if="isBuyingWithStars || (isLoading && selectedPriceType === ECOMMERCE_ITEM_PRICE_TYPE.STAR)"
        key-name="lingo.giveaway.transaction.loading_cta"
        default-value="Processing..."
      />
      <T
        v-else
        key-name="shop.unlock_for_free"
        default-value="Unlock for free"
      />
    </BaseButton>
    <BaseButton
      v-else-if="!isBasePlatform || (starPrice.amount <= starsBalance)"
      class="w-full"
      :loading="isBuyingWithStars"
      @click="handlePurchase(ECOMMERCE_ITEM_PRICE_TYPE.STAR)"
    >
      <span
        v-if="!isBuyingWithStars"
        class="flex items-center gap-x-2"
      >
        <T
          key-name="starter_pack.buy_with"
          default-value="Buy with"
        />
        <ThePremium :with-tooltip="false" />
        <span class="-ml-0.5">{{ formatUnshortenedNumber(starPrice.amount) }}</span>
      </span>
      <T
        v-else
        key-name="lingo.giveaway.transaction.loading_cta"
        default-value="Processing..."
      />
    </BaseButton>

    <BaseButton
      v-if="isBasePlatform && usdtPrice"
      style-type="secondary"
      :loading="isBuyingWithUsdt"
      class="relative w-full"
      @click="handlePurchase(ECOMMERCE_ITEM_PRICE_TYPE.USDT)"
    >
      <span
        v-if="!isBuyingWithUsdt"
        class="flex items-center gap-x-2"
      >
        <T
          key-name="starter_pack.buy_with"
          default-value="Buy with"
        />
        <img
          src="@/assets/images/usdc.png"
          class="size-5 min-w-[20px] object-cover"
          alt="USDC symbol"
        >
        <span>{{ usdtPrice?.amount }}</span>
      </span>
      <T
        v-else
        key-name="lingo.giveaway.transaction.loading_cta"
        default-value="Processing..."
      />
    </BaseButton>

    <template v-if="showTonPurchase && !isBasePlatform">
      <div class="relative w-full">
        <div class="absolute right-3 -top-1 rotate-[20deg]">
          <TonDiscountBadge label="-10%" />
        </div>
        <BaseButton
          :style-type="tonButtonDarkOutlineStyle ? 'dark-outline' : 'secondary'"
          :loading="isBuyingWithTon"
          class="relative w-full"
          @click="handlePurchase(ECOMMERCE_ITEM_PRICE_TYPE.TON)"
        >
          <span
            v-if="!isBuyingWithTon"
            class="flex items-center gap-x-2"
          >
            <T
              key-name="starter_pack.buy_with"
              default-value="Buy with"
            />
            <img
              src="@/assets/images/ton-symbol.png"
              class="size-5 min-w-[20px] object-cover"
              alt="TON symbol"
            >
            <span>{{ tonPrice?.amount }}</span>
          </span>
          <T
            v-else
            key-name="lingo.giveaway.transaction.loading_cta"
            default-value="Processing..."
          />
        </BaseButton>
      </div>

      <div
        class="mx-auto -mt-1 max-w-[307px] text-center text-xs -tracking-[0.28px] text-white/[.87] font-medium"
      >
        <T
          key-name="transaction.process_description"
          default-value="Transaction may take a few minutes to process. You'll be notified once it's successful."
        />
      </div>
    </template>
  </div>
</template>

